
import { useState } from 'react';
import { AssessmentStep, AssessmentSectionId } from '@/types/assessment-types';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { calculateTotalScore } from '@/utils/assessmentScoring';
import { useNavigate } from 'react-router-dom';
import type { AssessmentScores } from '@/utils/assessmentScoring';

interface SectionData {
  score: number;
  responses: Record<string, number>;
}

export const useAssessmentState = () => {
  const [currentStep, setCurrentStep] = useState<AssessmentStep>('start');
  const [assessmentData, setAssessmentData] = useState<AssessmentScores>({});
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleStart = () => {
    console.log("Starting assessment");
    setCurrentStep('productCraft');
    toast({
      title: "Assessment Started",
      description: "Welcome to your Product Leadership Assessment",
    });
  };

  const nextSteps: Record<AssessmentStep, AssessmentStep> = {
    'start': 'productCraft',
    'productCraft': 'customerDiscovery',
    'customerDiscovery': 'researchDesign',
    'researchDesign': 'marketImpact',
    'marketImpact': 'productOperations',
    'productOperations': 'industryImpact',
    'industryImpact': 'results',
    'results': 'results'
  };

  const handleSectionComplete = (sectionId: AssessmentSectionId, data: SectionData) => {
    console.log(`Completing section ${sectionId} with data:`, data);
    
    const updatedAssessmentData = {
      ...assessmentData,
      [sectionId]: data
    };
    setAssessmentData(updatedAssessmentData);

    const currentStepTyped = currentStep as AssessmentStep;
    const nextStep = nextSteps[currentStepTyped];
    
    if (nextStep) {
      console.log(`Moving to next section: ${nextStep}`);
      setCurrentStep(nextStep);
    }
  };

  const handleSaveResults = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log("User not authenticated, redirecting to auth page");
      toast({
        title: "Sign in required",
        description: "Please sign in to save your results and get personalized guidance",
      });
      navigate('/auth', { state: { from: '/assessment/product-management', assessmentData } });
      return;
    }

    try {
      console.log("Saving assessment results for user:", user.id);
      const totalScore = calculateTotalScore(assessmentData);
      const { error } = await supabase
        .from('profiles')
        .update({
          total_score: totalScore,
          assessment_completed: true,
          last_assessment_date: new Date().toISOString(),
          next_assessment_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      console.log("Assessment results saved successfully");
      toast({
        title: "Assessment Complete",
        description: "Your results have been saved successfully.",
      });
      navigate('/guide');
    } catch (error: any) {
      console.error('Error saving assessment:', error);
      toast({
        title: "Error",
        description: "Failed to save assessment results. Please try again.",
        variant: "destructive"
      });
    }
  };

  return {
    currentStep,
    setCurrentStep,
    assessmentData,
    handleStart,
    handleSectionComplete,
    handleSaveResults
  };
};
